// components/ProjectSubmissionWizard.jsx
// 5-Step SEC-compliant project submission intake wizard
// Design system: Navy/Cyan/Lime — Outfit, Instrument Sans, JetBrains Mono

import React, { useState, useEffect, useCallback } from 'react';

// ──────────────────────────────────────────────
// Design Tokens
// ──────────────────────────────────────────────

const COLORS = {
  bg: '#0F172A',
  bgCard: '#1E293B',
  bgInput: 'rgba(15, 23, 42, 0.6)',
  border: 'rgba(255, 255, 255, 0.08)',
  borderFocus: 'rgba(6, 182, 212, 0.4)',
  cyan: '#06B6D4',
  cyanMuted: 'rgba(6, 182, 212, 0.15)',
  lime: '#84CC16',
  limeMuted: 'rgba(132, 204, 22, 0.15)',
  text: '#F1F5F9',
  textMuted: '#94A3B8',
  textDim: '#64748B',
  error: '#EF4444',
  errorBg: 'rgba(239, 68, 68, 0.1)',
  errorBorder: 'rgba(239, 68, 68, 0.3)',
  success: '#22C55E',
};

const FONTS = {
  heading: "'Outfit', system-ui, sans-serif",
  body: "'Instrument Sans', system-ui, sans-serif",
  mono: "'JetBrains Mono', monospace",
};

// ──────────────────────────────────────────────
// Shared Style Objects
// ──────────────────────────────────────────────

const styles = {
  label: {
    display: 'block',
    fontFamily: FONTS.body,
    color: COLORS.text,
    marginBottom: '0.4rem',
    fontWeight: '600',
    fontSize: '0.875rem',
  },
  labelRequired: {
    color: COLORS.cyan,
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '0.7rem 0.85rem',
    background: COLORS.bgInput,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    color: COLORS.text,
    fontFamily: FONTS.body,
    fontSize: '0.9375rem',
    outline: 'none',
    transition: 'border-color 250ms ease, box-shadow 250ms ease',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '0.7rem 0.85rem',
    background: COLORS.bgInput,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    color: COLORS.text,
    fontFamily: FONTS.body,
    fontSize: '0.9375rem',
    outline: 'none',
    resize: 'vertical',
    minHeight: '100px',
    lineHeight: '1.55',
    transition: 'border-color 250ms ease, box-shadow 250ms ease',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '0.7rem 0.85rem',
    background: COLORS.bgInput,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    color: COLORS.text,
    fontFamily: FONTS.body,
    fontSize: '0.9375rem',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
    appearance: 'none',
  },
  fieldGroup: {
    marginBottom: '1.25rem',
  },
  helpText: {
    fontFamily: FONTS.body,
    fontSize: '0.8125rem',
    color: COLORS.textDim,
    marginTop: '0.3rem',
    lineHeight: '1.4',
  },
  sectionTitle: {
    fontFamily: FONTS.heading,
    fontSize: '1.125rem',
    fontWeight: '700',
    color: COLORS.text,
    marginBottom: '1rem',
    marginTop: '1.5rem',
    paddingBottom: '0.5rem',
    borderBottom: `1px solid ${COLORS.border}`,
  },
  charCount: {
    fontFamily: FONTS.mono,
    fontSize: '0.75rem',
    color: COLORS.textDim,
    textAlign: 'right',
    marginTop: '0.2rem',
  },
};

// ──────────────────────────────────────────────
// Helper Components
// ──────────────────────────────────────────────

function Label({ children, required }) {
  return (
    <label style={styles.label}>
      {children}
      {required && <span style={styles.labelRequired}> *</span>}
    </label>
  );
}

function HelpText({ children }) {
  return <div style={styles.helpText}>{children}</div>;
}

function CharCount({ current, min, max }) {
  const color = current < (min || 0) ? COLORS.error : current > (max || Infinity) ? COLORS.error : COLORS.textDim;
  return (
    <div style={{ ...styles.charCount, color }}>
      {current}{max ? ` / ${max}` : ''}{min ? ` (min ${min})` : ''}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, required, min, max, ...props }) {
  return (
    <input
      style={styles.input}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      minLength={min}
      maxLength={max}
      onFocus={(e) => {
        e.target.style.borderColor = COLORS.borderFocus;
        e.target.style.boxShadow = '0 0 0 2px rgba(6, 182, 212, 0.1)';
      }}
      onBlur={(e) => {
        e.target.style.borderColor = COLORS.border;
        e.target.style.boxShadow = 'none';
      }}
      {...props}
    />
  );
}

function TextArea({ value, onChange, placeholder, rows, min, max }) {
  return (
    <>
      <textarea
        style={{ ...styles.textarea, minHeight: rows ? `${rows * 1.8}rem` : '100px' }}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        onFocus={(e) => {
          e.target.style.borderColor = COLORS.borderFocus;
          e.target.style.boxShadow = '0 0 0 2px rgba(6, 182, 212, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = COLORS.border;
          e.target.style.boxShadow = 'none';
        }}
      />
      {(min || max) && <CharCount current={(value || '').length} min={min} max={max} />}
    </>
  );
}

function SelectInput({ value, onChange, options, placeholder }) {
  return (
    <select
      style={styles.select}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="" disabled>{placeholder || 'Select...'}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

function MultiSelect({ value, onChange, options }) {
  const selected = value || [];
  const toggle = (opt) => {
    if (selected.includes(opt)) {
      onChange(selected.filter((v) => v !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
      {options.map((opt) => {
        const isSelected = selected.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => toggle(opt)}
            style={{
              padding: '0.4rem 0.85rem',
              background: isSelected ? COLORS.cyanMuted : COLORS.bgInput,
              border: `1px solid ${isSelected ? COLORS.borderFocus : COLORS.border}`,
              borderRadius: '8px',
              color: isSelected ? COLORS.cyan : COLORS.textMuted,
              fontFamily: FONTS.body,
              fontSize: '0.8125rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 200ms ease',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function NumberInput({ value, onChange, placeholder, min, max, prefix }) {
  return (
    <div style={{ position: 'relative' }}>
      {prefix && (
        <span style={{
          position: 'absolute',
          left: '0.85rem',
          top: '50%',
          transform: 'translateY(-50%)',
          fontFamily: FONTS.mono,
          color: COLORS.textDim,
          fontSize: '0.9375rem',
          pointerEvents: 'none',
        }}>
          {prefix}
        </span>
      )}
      <input
        type="number"
        style={{ ...styles.input, paddingLeft: prefix ? '1.75rem' : '0.85rem' }}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        placeholder={placeholder}
        min={min}
        max={max}
        onFocus={(e) => {
          e.target.style.borderColor = COLORS.borderFocus;
          e.target.style.boxShadow = '0 0 0 2px rgba(6, 182, 212, 0.1)';
        }}
        onBlur={(e) => {
          e.target.style.borderColor = COLORS.border;
          e.target.style.boxShadow = 'none';
        }}
      />
    </div>
  );
}

function ErrorBanner({ errors }) {
  if (!errors || errors.length === 0) return null;
  return (
    <div style={{
      padding: '1rem',
      background: COLORS.errorBg,
      border: `1px solid ${COLORS.errorBorder}`,
      borderRadius: '10px',
      marginBottom: '1.25rem',
    }}>
      {errors.map((err, i) => (
        <div key={i} style={{
          fontFamily: FONTS.body,
          fontSize: '0.875rem',
          color: '#fca5a5',
          marginBottom: i < errors.length - 1 ? '0.3rem' : 0,
        }}>
          {err}
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────
// Form Config (loaded from backend or hardcoded)
// ──────────────────────────────────────────────

const GENRES = [
  'Drama', 'Comedy', 'Thriller', 'Horror', 'Sci-Fi',
  'Documentary', 'Animation', 'Action', 'Romance', 'Experimental', 'Other'
];

const PROJECT_TYPES = [
  'Feature Film', 'Short Film', 'Series (Limited)', 'Series (Ongoing)',
  'Documentary Feature', 'Documentary Series', 'Animation'
];

const TARGET_RATINGS = ['G', 'PG', 'PG-13', 'R', 'NC-17', 'Not Rated'];

const SECURITY_TYPES = [
  'Revenue Participation Token', 'Equity Token', 'SAFE + Token', 'Convertible Note + Token'
];

const DISTRIBUTION_TIERS = [
  'Major Independent (A24, NEON, Focus)',
  'Mid-Tier (IFC, Magnolia, Oscilloscope)',
  'Digital-First (streaming platforms)',
  'Platform Native (INDI streaming)',
  'Open to INDI Distro Recommendation',
];

const ENTITY_TYPES = ['LLC', 'S-Corp', 'C-Corp', 'Limited Partnership', 'Other'];

const OVERSUBSCRIPTION_METHODS = ['First-come first-served', 'Pro-rata', 'Waitlist priority', 'Other'];

const REVENUE_FREQUENCIES = ['Monthly', 'Quarterly', 'Semi-annually', 'Upon receipt'];

const TEAM_ROLES = [
  'Director', 'Producer', 'Executive Producer', 'Writer', 'Cinematographer',
  'Editor', 'Lead Actor', 'Composer', 'Production Designer', 'Other'
];

// ──────────────────────────────────────────────
// STEP 1: Project Basics
// ──────────────────────────────────────────────

function Step1ProjectBasics({ data, onChange }) {
  const update = (field, value) => onChange({ ...data, [field]: value });

  return (
    <div>
      <div style={styles.sectionTitle}>1.1 Core Information</div>

      <div style={styles.fieldGroup}>
        <Label required>Project Title</Label>
        <TextInput
          value={data.title}
          onChange={(v) => update('title', v)}
          placeholder="The working title of your project"
          max={120}
        />
        <CharCount current={(data.title || '').length} min={3} max={120} />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Logline</Label>
        <TextInput
          value={data.logline}
          onChange={(v) => update('logline', v)}
          placeholder="One-sentence pitch that captures the essence of the story"
          max={200}
        />
        <CharCount current={(data.logline || '').length} min={25} max={200} />
      </div>

      <div style={styles.fieldGroup}>
        <Label>Tagline</Label>
        <TextInput
          value={data.tagline}
          onChange={(v) => update('tagline', v)}
          placeholder="Marketing-oriented hook for the project page"
          max={100}
        />
        <HelpText>Short marketing hook shown on the project card.</HelpText>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Genre</Label>
        <MultiSelect
          value={data.genre}
          onChange={(v) => update('genre', v)}
          options={GENRES}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Project Type</Label>
          <SelectInput
            value={data.projectType}
            onChange={(v) => update('projectType', v)}
            options={PROJECT_TYPES}
            placeholder="Select type..."
          />
        </div>
        <div>
          <Label required>Runtime Estimate (min)</Label>
          <NumberInput
            value={data.runtimeEstimate}
            onChange={(v) => update('runtimeEstimate', v)}
            placeholder="e.g., 95"
            min={1}
            max={240}
          />
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <Label>Target Rating</Label>
        <SelectInput
          value={data.targetRating}
          onChange={(v) => update('targetRating', v)}
          options={TARGET_RATINGS}
          placeholder="Select rating..."
        />
        <HelpText>Helps investors understand audience scope.</HelpText>
      </div>

      <div style={styles.sectionTitle}>1.2 Story & Vision</div>

      <div style={styles.fieldGroup}>
        <Label required>Synopsis</Label>
        <TextArea
          value={data.synopsis}
          onChange={(v) => update('synopsis', v)}
          placeholder="Full narrative summary including beginning, middle, and end. Spoilers are expected for investor evaluation."
          rows={8}
          min={200}
          max={10000}
        />
        <HelpText>200–2,000 words. Include the complete story arc for investor evaluation.</HelpText>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Director's Vision Statement</Label>
        <TextArea
          value={data.directorsVision}
          onChange={(v) => update('directorsVision', v)}
          placeholder="Your creative vision, tone, visual approach, and what makes this project unique."
          rows={5}
          min={100}
          max={5000}
        />
        <HelpText>100–1,000 words. What's your creative approach and why is this story yours to tell?</HelpText>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Target Audience</Label>
        <TextArea
          value={data.targetAudience}
          onChange={(v) => update('targetAudience', v)}
          placeholder="Primary and secondary audiences, comparable projects, market positioning."
          rows={4}
          min={50}
          max={2500}
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label>Comparable Projects</Label>
        <TextArea
          value={data.comparableProjects}
          onChange={(v) => update('comparableProjects', v)}
          placeholder='List 2–5 comparable films/shows. E.g., "Comparable to Moonlight ($65M worldwide) meets The Florida Project."'
          rows={3}
        />
        <HelpText>Include box office or streaming data if available.</HelpText>
      </div>

      <div style={styles.sectionTitle}>1.3 Visual Assets</div>
      <div style={{
        padding: '1.25rem',
        background: COLORS.cyanMuted,
        border: `1px solid ${COLORS.borderFocus}`,
        borderRadius: '10px',
        fontFamily: FONTS.body,
        fontSize: '0.875rem',
        color: COLORS.textMuted,
        lineHeight: '1.55',
      }}>
        <strong style={{ color: COLORS.cyan }}>Coming Soon:</strong> File uploads for key art/poster,
        pitch video, mood board, and script sample will be available after deployment. For now,
        you can include links to these materials in your synopsis or vision statement.
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// STEP 2: Team & Credentials
// ──────────────────────────────────────────────

function Step2TeamCredentials({ data, onChange }) {
  const creator = data.primaryCreator || {};
  const updateCreator = (field, value) => {
    onChange({ ...data, primaryCreator: { ...creator, [field]: value } });
  };

  const pastProjects = creator.pastProjects || [{}];
  const updatePastProject = (index, field, value) => {
    const updated = [...pastProjects];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...data, primaryCreator: { ...creator, pastProjects: updated } });
  };
  const addPastProject = () => {
    onChange({
      ...data,
      primaryCreator: { ...creator, pastProjects: [...pastProjects, {}] },
    });
  };
  const removePastProject = (index) => {
    onChange({
      ...data,
      primaryCreator: { ...creator, pastProjects: pastProjects.filter((_, i) => i !== index) },
    });
  };

  const teamMembers = data.teamMembers || [];
  const addTeamMember = () => {
    onChange({ ...data, teamMembers: [...teamMembers, {}] });
  };
  const updateTeamMember = (index, field, value) => {
    const updated = [...teamMembers];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...data, teamMembers: updated });
  };
  const removeTeamMember = (index) => {
    onChange({ ...data, teamMembers: teamMembers.filter((_, i) => i !== index) });
  };

  const company = data.productionCompany || {};
  const updateCompany = (field, value) => {
    onChange({ ...data, productionCompany: { ...company, [field]: value } });
  };

  return (
    <div>
      <div style={styles.sectionTitle}>2.1 Primary Creator</div>
      <HelpText>This maps to the "principal executive officer" in SEC filing language.</HelpText>

      <div style={{ ...styles.fieldGroup, marginTop: '1rem' }}>
        <Label required>Full Legal Name</Label>
        <TextInput
          value={creator.fullLegalName}
          onChange={(v) => updateCreator('fullLegalName', v)}
          placeholder="Must match government ID for KYC verification"
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Role / Title</Label>
        <TextInput
          value={creator.role}
          onChange={(v) => updateCreator('role', v)}
          placeholder="e.g., Director, Producer, Writer/Director"
        />
        <HelpText>This becomes your officer title for Form C filing.</HelpText>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Professional Bio</Label>
        <TextArea
          value={creator.bio}
          onChange={(v) => updateCreator('bio', v)}
          placeholder="Business experience for the past 3 years including principal occupations and employers."
          rows={5}
          min={100}
          max={4000}
        />
        <HelpText>SEC Rule 201(b) requires 3-year business experience disclosure.</HelpText>
      </div>

      <div style={styles.fieldGroup}>
        <Label>IMDb / Portfolio URL</Label>
        <TextInput
          value={creator.imdbUrl}
          onChange={(v) => updateCreator('imdbUrl', v)}
          placeholder="https://www.imdb.com/name/..."
          type="url"
        />
      </div>

      {/* Past Projects */}
      <div style={{ ...styles.sectionTitle, fontSize: '1rem', marginTop: '1.25rem' }}>
        Past Projects <span style={styles.labelRequired}>*</span>
      </div>
      <HelpText>At least 1 past project is required.</HelpText>

      {pastProjects.map((proj, i) => (
        <div key={i} style={{
          padding: '1rem',
          background: 'rgba(6, 182, 212, 0.03)',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '10px',
          marginBottom: '0.75rem',
          marginTop: '0.75rem',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem',
          }}>
            <span style={{
              fontFamily: FONTS.mono,
              fontSize: '0.75rem',
              color: COLORS.textDim,
            }}>
              Project {i + 1}
            </span>
            {pastProjects.length > 1 && (
              <button
                type="button"
                onClick={() => removePastProject(i)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: COLORS.error,
                  fontFamily: FONTS.body,
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                }}
              >
                Remove
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <Label>Title</Label>
              <TextInput value={proj.title} onChange={(v) => updatePastProject(i, 'title', v)} placeholder="Project title" />
            </div>
            <div>
              <Label>Year</Label>
              <NumberInput value={proj.year} onChange={(v) => updatePastProject(i, 'year', v)} placeholder="2024" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <Label>Your Role</Label>
              <TextInput value={proj.role} onChange={(v) => updatePastProject(i, 'role', v)} placeholder="Director" />
            </div>
            <div>
              <Label>Distribution</Label>
              <TextInput value={proj.distribution} onChange={(v) => updatePastProject(i, 'distribution', v)} placeholder="Festival, streaming..." />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <Label>Recognition</Label>
            <TextInput value={proj.recognition} onChange={(v) => updatePastProject(i, 'recognition', v)} placeholder="Awards, selections, nominations" />
          </div>
        </div>
      ))}

      {pastProjects.length < 10 && (
        <button
          type="button"
          onClick={addPastProject}
          style={{
            display: 'block',
            width: '100%',
            padding: '0.6rem',
            background: COLORS.cyanMuted,
            border: `1px dashed ${COLORS.borderFocus}`,
            borderRadius: '10px',
            color: COLORS.cyan,
            fontFamily: FONTS.body,
            fontSize: '0.875rem',
            fontWeight: '500',
            cursor: 'pointer',
            marginBottom: '1.5rem',
          }}
        >
          + Add Past Project
        </button>
      )}

      {/* Team Members */}
      <div style={styles.sectionTitle}>2.2 Key Team Members</div>
      <HelpText>Each team member listed here will be disclosed in the Form C filing.</HelpText>

      {teamMembers.map((member, i) => (
        <div key={i} style={{
          padding: '1rem',
          background: 'rgba(6, 182, 212, 0.03)',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '10px',
          marginBottom: '0.75rem',
          marginTop: '0.75rem',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.75rem',
          }}>
            <span style={{ fontFamily: FONTS.mono, fontSize: '0.75rem', color: COLORS.textDim }}>
              Team Member {i + 1}
            </span>
            <button
              type="button"
              onClick={() => removeTeamMember(i)}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.error,
                fontFamily: FONTS.body,
                fontSize: '0.8125rem',
                cursor: 'pointer',
              }}
            >
              Remove
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <Label required>Full Legal Name</Label>
              <TextInput value={member.fullLegalName} onChange={(v) => updateTeamMember(i, 'fullLegalName', v)} placeholder="Legal name" />
            </div>
            <div>
              <Label required>Role</Label>
              <SelectInput value={member.role} onChange={(v) => updateTeamMember(i, 'role', v)} options={TEAM_ROLES} placeholder="Select role..." />
            </div>
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <Label required>Professional Bio</Label>
            <TextArea value={member.bio} onChange={(v) => updateTeamMember(i, 'bio', v)} placeholder="3-year business experience" rows={3} min={50} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
            <div>
              <Label>IMDb / Portfolio URL</Label>
              <TextInput value={member.imdbUrl} onChange={(v) => updateTeamMember(i, 'imdbUrl', v)} placeholder="https://..." type="url" />
            </div>
            <div>
              <Label required>Equity Ownership %</Label>
              <NumberInput value={member.equityOwnershipPercent} onChange={(v) => updateTeamMember(i, 'equityOwnershipPercent', v)} placeholder="0" min={0} max={100} />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <Label>Compensation Arrangement</Label>
            <TextInput value={member.compensationArrangement} onChange={(v) => updateTeamMember(i, 'compensationArrangement', v)} placeholder="Deferred salary, points, flat fee..." />
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={addTeamMember}
        style={{
          display: 'block',
          width: '100%',
          padding: '0.6rem',
          background: COLORS.cyanMuted,
          border: `1px dashed ${COLORS.borderFocus}`,
          borderRadius: '10px',
          color: COLORS.cyan,
          fontFamily: FONTS.body,
          fontSize: '0.875rem',
          fontWeight: '500',
          cursor: 'pointer',
          marginBottom: '1.5rem',
        }}
      >
        + Add Team Member
      </button>

      {/* Production Company */}
      <div style={styles.sectionTitle}>2.3 Production Company (Optional)</div>
      <div style={{
        padding: '0.85rem 1rem',
        background: COLORS.cyanMuted,
        border: `1px solid ${COLORS.borderFocus}`,
        borderRadius: '10px',
        fontFamily: FONTS.body,
        fontSize: '0.8125rem',
        color: COLORS.textMuted,
        lineHeight: '1.5',
        marginBottom: '1rem',
      }}>
        This is your existing production company, if you have one. It is <em>not</em> the project-specific
        SPV (configured in Step 5). Many first-time filmmakers won't have one — that's perfectly fine.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label>Company Name</Label>
          <TextInput value={company.companyName} onChange={(v) => updateCompany('companyName', v)} placeholder="Production company name" />
        </div>
        <div>
          <Label>Website</Label>
          <TextInput value={company.website} onChange={(v) => updateCompany('website', v)} placeholder="https://..." type="url" />
        </div>
      </div>
      <div style={styles.fieldGroup}>
        <Label>Number of Employees</Label>
        <NumberInput value={company.numberOfEmployees} onChange={(v) => updateCompany('numberOfEmployees', v)} placeholder="0" min={0} />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// STEP 3: Budget & Financing
// ──────────────────────────────────────────────

function Step3BudgetFinancing({ data, onChange }) {
  const overview = data.budgetOverview || {};
  const updateOverview = (field, value) => {
    onChange({ ...data, budgetOverview: { ...overview, [field]: value } });
  };

  const breakdown = data.budgetBreakdown || {};
  const updateBreakdown = (field, value) => {
    onChange({ ...data, budgetBreakdown: { ...breakdown, [field]: value } });
  };

  const securities = data.securitiesDetails || {};
  const updateSecurities = (field, value) => {
    onChange({ ...data, securitiesDetails: { ...securities, [field]: value } });
  };

  const milestones = data.milestones || [{}];
  const updateMilestone = (index, field, value) => {
    const updated = [...milestones];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...data, milestones: updated });
  };
  const addMilestone = () => {
    onChange({ ...data, milestones: [...milestones, {}] });
  };
  const removeMilestone = (index) => {
    onChange({ ...data, milestones: milestones.filter((_, i) => i !== index) });
  };

  // Auto-calculations
  const fundingGoal = overview.fundingGoal || 0;
  const totalBudget = overview.totalProductionBudget || 0;
  const platformFee = Math.round(fundingGoal * 0.05);
  const complianceFee = 5000;
  const contingencyPercent = breakdown.contingencyPercent || 10;
  const contingencyAmount = Math.round(totalBudget * (contingencyPercent / 100));

  return (
    <div>
      <div style={styles.sectionTitle}>3.1 Budget Overview</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Total Production Budget</Label>
          <NumberInput
            value={overview.totalProductionBudget}
            onChange={(v) => updateOverview('totalProductionBudget', v)}
            placeholder="Total cost to deliver"
            prefix="$"
            min={1000}
          />
        </div>
        <div>
          <Label required>Funding Goal (Target)</Label>
          <NumberInput
            value={overview.fundingGoal}
            onChange={(v) => updateOverview('fundingGoal', v)}
            placeholder="Min to proceed"
            prefix="$"
            min={1000}
            max={5000000}
          />
          <HelpText>Max $5M under Reg CF.</HelpText>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Maximum Offering Amount</Label>
          <NumberInput
            value={overview.maximumOfferingAmount}
            onChange={(v) => updateOverview('maximumOfferingAmount', v)}
            placeholder="Max if oversubscribed"
            prefix="$"
            max={5000000}
          />
        </div>
        <div>
          <Label required>Oversubscription Allocation</Label>
          <SelectInput
            value={overview.oversubscriptionAllocation}
            onChange={(v) => updateOverview('oversubscriptionAllocation', v)}
            options={OVERSUBSCRIPTION_METHODS}
            placeholder="Select method..."
          />
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Offering Deadline</Label>
        <input
          type="date"
          style={styles.input}
          value={overview.offeringDeadline || ''}
          onChange={(e) => updateOverview('offeringDeadline', e.target.value)}
        />
        <HelpText>Standard is 60–90 days from campaign launch.</HelpText>
      </div>

      <div style={styles.fieldGroup}>
        <Label>Other Funding Sources</Label>
        <TextArea
          value={overview.otherFundingSources}
          onChange={(v) => updateOverview('otherFundingSources', v)}
          placeholder="Disclose any other capital already secured or being sought (grants, pre-sales, tax credits, private investors)."
          rows={3}
        />
      </div>

      {/* Platform Fees — Read-only */}
      <div style={styles.sectionTitle}>3.3 Platform Fees (Auto-Calculated)</div>
      <div style={{
        padding: '1.25rem',
        background: 'rgba(132, 204, 22, 0.04)',
        border: `1px solid rgba(132, 204, 22, 0.15)`,
        borderRadius: '10px',
        marginBottom: '1.25rem',
      }}>
        {[
          { label: 'INDI Platform Fee', detail: '5% of amount raised', amount: fundingGoal ? `$${platformFee.toLocaleString()}` : '—' },
          { label: 'INDI Distribution Services', detail: '% of budget (TBD)', amount: 'TBD' },
          { label: 'Compliance & Reporting', detail: 'Flat fee', amount: `$${complianceFee.toLocaleString()}` },
        ].map((fee, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.5rem 0',
            borderBottom: i < 2 ? `1px solid rgba(132, 204, 22, 0.1)` : 'none',
          }}>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: '0.875rem', color: COLORS.text, fontWeight: '500' }}>
                {fee.label}
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: '0.75rem', color: COLORS.textDim }}>
                {fee.detail}
              </div>
            </div>
            <div style={{ fontFamily: FONTS.mono, fontSize: '0.9375rem', color: COLORS.lime, fontWeight: '600' }}>
              {fee.amount}
            </div>
          </div>
        ))}
        {fundingGoal > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '0.75rem',
            paddingTop: '0.75rem',
            borderTop: `1px solid rgba(132, 204, 22, 0.2)`,
          }}>
            <span style={{ fontFamily: FONTS.body, fontSize: '0.875rem', color: COLORS.text, fontWeight: '700' }}>
              Total Estimated Fees
            </span>
            <span style={{ fontFamily: FONTS.mono, fontSize: '1rem', color: COLORS.lime, fontWeight: '700' }}>
              ${(platformFee + complianceFee).toLocaleString()}+
            </span>
          </div>
        )}
      </div>

      {/* Contingency */}
      <div style={styles.sectionTitle}>Contingency</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Contingency %</Label>
          <NumberInput
            value={contingencyPercent}
            onChange={(v) => updateBreakdown('contingencyPercent', Math.max(v, 10))}
            placeholder="10"
            min={10}
            max={50}
          />
          <HelpText>Minimum 10% enforced by platform.</HelpText>
        </div>
        <div>
          <Label>Contingency Amount</Label>
          <div style={{
            padding: '0.7rem 0.85rem',
            background: COLORS.bgInput,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '10px',
            fontFamily: FONTS.mono,
            fontSize: '0.9375rem',
            color: COLORS.lime,
          }}>
            ${contingencyAmount.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Securities Details */}
      <div style={styles.sectionTitle}>3.4 Securities Details</div>
      <div style={{
        padding: '0.85rem 1rem',
        background: COLORS.cyanMuted,
        border: `1px solid ${COLORS.borderFocus}`,
        borderRadius: '10px',
        fontFamily: FONTS.body,
        fontSize: '0.8125rem',
        color: COLORS.textMuted,
        lineHeight: '1.5',
        marginBottom: '1rem',
      }}>
        Select your security type below. Templates will be pre-populated for investor rights and
        dilution disclosures. You'll review and customize with project-specific details.
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Security Type</Label>
        <SelectInput
          value={securities.securityType}
          onChange={(v) => updateSecurities('securityType', v)}
          options={SECURITY_TYPES}
          placeholder="Select security type..."
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Price Per Unit</Label>
          <NumberInput value={securities.pricePerUnit} onChange={(v) => updateSecurities('pricePerUnit', v)} placeholder="e.g., 1.00" prefix="$" min={0.01} />
        </div>
        <div>
          <Label required>Minimum Investment</Label>
          <NumberInput value={securities.minimumInvestment} onChange={(v) => updateSecurities('minimumInvestment', v)} placeholder="e.g., 100" prefix="$" min={1} />
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Investor Rights Summary</Label>
        <TextArea
          value={securities.investorRightsSummary}
          onChange={(v) => updateSecurities('investorRightsSummary', v)}
          placeholder="Voting rights, information rights, transfer restrictions, and modification terms. Pre-populated templates coming soon."
          rows={5}
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Dilution Risk Disclosure</Label>
        <TextArea
          value={securities.dilutionRiskDisclosure}
          onChange={(v) => updateSecurities('dilutionRiskDisclosure', v)}
          placeholder="How future issuances could dilute current investors, risks of minority ownership, and corporate action risks."
          rows={4}
        />
      </div>

      {/* Production Milestones */}
      <div style={styles.sectionTitle}>3.5 Production Milestones</div>
      <HelpText>Minimum 3 milestones required. Fund release percentages must total 100%.</HelpText>

      {milestones.map((ms, i) => (
        <div key={i} style={{
          padding: '1rem',
          background: 'rgba(6, 182, 212, 0.03)',
          border: `1px solid ${COLORS.border}`,
          borderRadius: '10px',
          marginBottom: '0.75rem',
          marginTop: '0.75rem',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
            <span style={{ fontFamily: FONTS.mono, fontSize: '0.75rem', color: COLORS.textDim }}>
              Milestone {i + 1}
            </span>
            {milestones.length > 1 && (
              <button type="button" onClick={() => removeMilestone(i)}
                style={{ background: 'none', border: 'none', color: COLORS.error, fontFamily: FONTS.body, fontSize: '0.8125rem', cursor: 'pointer' }}>
                Remove
              </button>
            )}
          </div>
          <div style={{ marginBottom: '0.75rem' }}>
            <Label required>Title</Label>
            <TextInput value={ms.title} onChange={(v) => updateMilestone(i, 'title', v)} placeholder='e.g., "Pre-production complete"' />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <div>
              <Label required>Target Date</Label>
              <input type="date" style={styles.input} value={ms.targetDate || ''} onChange={(e) => updateMilestone(i, 'targetDate', e.target.value)} />
            </div>
            <div>
              <Label required>Budget Allocation</Label>
              <NumberInput value={ms.budgetAllocation} onChange={(v) => updateMilestone(i, 'budgetAllocation', v)} prefix="$" />
            </div>
            <div>
              <Label required>Fund Release %</Label>
              <NumberInput value={ms.fundReleasePercent} onChange={(v) => updateMilestone(i, 'fundReleasePercent', v)} placeholder="%" min={0} max={100} />
            </div>
          </div>
          <div>
            <Label required>Deliverable Description</Label>
            <TextInput value={ms.deliverableDescription} onChange={(v) => updateMilestone(i, 'deliverableDescription', v)} placeholder="What output proves this milestone is reached?" />
          </div>
        </div>
      ))}

      <button type="button" onClick={addMilestone}
        style={{
          display: 'block', width: '100%', padding: '0.6rem',
          background: COLORS.cyanMuted, border: `1px dashed ${COLORS.borderFocus}`,
          borderRadius: '10px', color: COLORS.cyan, fontFamily: FONTS.body,
          fontSize: '0.875rem', fontWeight: '500', cursor: 'pointer',
        }}>
        + Add Milestone
      </button>
    </div>
  );
}

// ──────────────────────────────────────────────
// STEP 4: Revenue & Distribution
// ──────────────────────────────────────────────

function Step4RevenueDistribution({ data, onChange }) {
  const dist = data.distributionPreferences || {};
  const updateDist = (field, value) => {
    onChange({ ...data, distributionPreferences: { ...dist, [field]: value } });
  };

  const rev = data.revenueSharing || {};
  const updateRev = (field, value) => {
    onChange({ ...data, revenueSharing: { ...rev, [field]: value } });
  };

  const token = data.tokenStructure || {};
  const updateToken = (field, value) => {
    onChange({ ...data, tokenStructure: { ...token, [field]: value } });
  };

  const valuation = data.valuation || {};
  const updateValuation = (field, value) => {
    onChange({ ...data, valuation: { ...valuation, [field]: value } });
  };

  // Revenue share validation
  const investorPct = rev.investorRevenueSharePercent || 0;
  const creatorPct = rev.creatorRevenueSharePercent || 0;
  const platformPct = 5;
  const total = investorPct + creatorPct + platformPct;
  const isValid = Math.abs(total - 100) < 0.01;

  return (
    <div>
      <div style={styles.sectionTitle}>4.1 Distribution Preferences</div>
      <div style={{
        padding: '0.85rem 1rem',
        background: COLORS.limeMuted,
        border: `1px solid rgba(132, 204, 22, 0.2)`,
        borderRadius: '10px',
        fontFamily: FONTS.body,
        fontSize: '0.8125rem',
        color: COLORS.textMuted,
        lineHeight: '1.5',
        marginBottom: '1rem',
      }}>
        <strong style={{ color: COLORS.lime }}>INDI Distribution LLC</strong> handles all post-production
        distribution: festival strategy, distributor placement, revenue collection, and investor reporting.
        Your preferences below inform the strategy.
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Distribution Preferences</Label>
        <TextArea
          value={dist.preferences}
          onChange={(v) => updateDist('preferences', v)}
          placeholder="Your preferred distribution approach: theatrical premiere priority, festival-first strategy, streaming focus, hybrid..."
          rows={4}
          min={100}
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Distribution Tier Preference</Label>
        <SelectInput
          value={dist.tierPreference}
          onChange={(v) => updateDist('tierPreference', v)}
          options={DISTRIBUTION_TIERS}
          placeholder="Select tier..."
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label>Existing Distribution Relationships</Label>
        <TextArea
          value={dist.existingRelationships}
          onChange={(v) => updateDist('existingRelationships', v)}
          placeholder="LOIs, pre-sales, or distributor relationships already in place."
          rows={3}
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label>Festival Strategy Preferences</Label>
        <TextInput
          value={dist.festivalStrategy}
          onChange={(v) => updateDist('festivalStrategy', v)}
          placeholder="Target festivals, premiere tier, awards positioning..."
        />
      </div>

      {/* Revenue Sharing */}
      <div style={styles.sectionTitle}>4.2 Revenue Sharing</div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '1rem',
        marginBottom: '0.75rem',
      }}>
        <div>
          <Label required>Investor Share %</Label>
          <NumberInput value={rev.investorRevenueSharePercent} onChange={(v) => updateRev('investorRevenueSharePercent', v)} placeholder="50" min={0} max={95} />
        </div>
        <div>
          <Label required>Creator Share %</Label>
          <NumberInput value={rev.creatorRevenueSharePercent} onChange={(v) => updateRev('creatorRevenueSharePercent', v)} placeholder="45" min={0} max={95} />
        </div>
        <div>
          <Label>Platform Fee %</Label>
          <div style={{
            padding: '0.7rem 0.85rem',
            background: COLORS.bgInput,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '10px',
            fontFamily: FONTS.mono,
            fontSize: '0.9375rem',
            color: COLORS.textDim,
          }}>
            5% (fixed)
          </div>
        </div>
      </div>

      {/* Validation indicator */}
      <div style={{
        padding: '0.5rem 0.85rem',
        background: isValid ? 'rgba(34, 197, 94, 0.08)' : COLORS.errorBg,
        border: `1px solid ${isValid ? 'rgba(34, 197, 94, 0.2)' : COLORS.errorBorder}`,
        borderRadius: '8px',
        fontFamily: FONTS.mono,
        fontSize: '0.8125rem',
        color: isValid ? COLORS.success : '#fca5a5',
        marginBottom: '1.25rem',
      }}>
        Total: {total}% {isValid ? '✓' : `(must equal 100%)`}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label>Preferred Return %</Label>
          <NumberInput value={rev.preferredReturn} onChange={(v) => updateRev('preferredReturn', v)} placeholder="Optional" min={0} />
        </div>
        <div>
          <Label required>Distribution Frequency</Label>
          <SelectInput value={rev.revenueDistributionFrequency} onChange={(v) => updateRev('revenueDistributionFrequency', v)} options={REVENUE_FREQUENCIES} placeholder="Select..." />
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Revenue Waterfall Structure</Label>
        <TextArea
          value={rev.waterfallStructure}
          onChange={(v) => updateRev('waterfallStructure', v)}
          placeholder="Define payment priority: (1) Recoupment of investor principal, (2) Preferred return, (3) Revenue split..."
          rows={4}
        />
      </div>

      {/* Token Structure */}
      <div style={styles.sectionTitle}>4.3 Token Structure</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Project Token Symbol</Label>
          <TextInput
            value={token.projectTokenSymbol}
            onChange={(v) => updateToken('projectTokenSymbol', v.toUpperCase().slice(0, 6))}
            placeholder="e.g., SUNSET"
            max={6}
          />
          <HelpText>3–6 uppercase characters. Must be unique.</HelpText>
        </div>
        <div>
          <Label required>Total Token Supply</Label>
          <NumberInput value={token.totalTokenSupply} onChange={(v) => updateToken('totalTokenSupply', v)} placeholder="Auto-calculated" min={1} />
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <Label>Token Standard</Label>
        <div style={{
          padding: '0.7rem 0.85rem',
          background: COLORS.bgInput,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '10px',
          fontFamily: FONTS.mono,
          fontSize: '0.875rem',
          color: COLORS.textDim,
        }}>
          SPL Token (Solana)
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Transfer Restrictions</Label>
        <TextArea
          value={token.transferRestrictions || 'Securities purchased through this offering are subject to a 12-month restriction on resale under SEC Regulation CF. After the 12-month holding period, transfers may only occur in compliance with applicable securities laws.'}
          onChange={(v) => updateToken('transferRestrictions', v)}
          rows={3}
        />
        <HelpText>Pre-populated with standard Reg CF lock-up language. Customize if needed.</HelpText>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div style={styles.fieldGroup}>
          <Label>xINDI Eligibility</Label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={token.xindiEligibility || false} onChange={(e) => updateToken('xindiEligibility', e.target.checked)} />
            <span style={{ fontFamily: FONTS.body, fontSize: '0.875rem', color: COLORS.textMuted }}>Enable xINDI staking rewards</span>
          </label>
        </div>
        <div style={styles.fieldGroup}>
          <Label>Secondary Market Opt-In</Label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={token.secondaryMarketOptIn || false} onChange={(e) => updateToken('secondaryMarketOptIn', e.target.checked)} />
            <span style={{ fontFamily: FONTS.body, fontSize: '0.875rem', color: COLORS.textMuted }}>Opt into future marketplace</span>
          </label>
        </div>
      </div>

      {/* Valuation */}
      <div style={styles.sectionTitle}>4.4 Valuation</div>

      <div style={styles.fieldGroup}>
        <Label required>Pre-Money Valuation</Label>
        <NumberInput value={valuation.preMoneyValuation} onChange={(v) => updateValuation('preMoneyValuation', v)} prefix="$" placeholder="How you value the project pre-offering" />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Valuation Method</Label>
        <TextArea
          value={valuation.valuationMethod}
          onChange={(v) => updateValuation('valuationMethod', v)}
          placeholder="Methodology used: comparable projects, revenue multiples, cost-based. Include examples of how securities may be valued in the future."
          rows={4}
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Cap Table Summary</Label>
        <TextArea
          value={valuation.capTableSummary}
          onChange={(v) => updateValuation('capTableSummary', v)}
          placeholder="Pre-offering and post-offering ownership breakdown including all security classes."
          rows={4}
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// STEP 5: Compliance & Legal
// ──────────────────────────────────────────────

function Step5ComplianceLegal({ data, onChange }) {
  const entity = data.productionEntity || {};
  const updateEntity = (field, value) => {
    onChange({ ...data, productionEntity: { ...entity, [field]: value } });
  };

  const risks = data.riskFactors || {};
  const updateRisks = (field, value) => {
    onChange({ ...data, riskFactors: { ...risks, [field]: value } });
  };

  const financial = data.financialCondition || {};
  const updateFinancial = (field, value) => {
    onChange({ ...data, financialCondition: { ...financial, [field]: value } });
  };

  const regulatory = data.regulatoryDisclosures || {};
  const updateRegulatory = (field, value) => {
    onChange({ ...data, regulatoryDisclosures: { ...regulatory, [field]: value } });
  };

  const certs = data.certifications || {};
  const updateCerts = (field, value) => {
    onChange({ ...data, certifications: { ...certs, [field]: value } });
  };

  return (
    <div>
      {/* 5.1 Production Entity */}
      <div style={styles.sectionTitle}>5.1 Production Entity (SPV)</div>
      <div style={{
        padding: '0.85rem 1rem',
        background: COLORS.cyanMuted,
        border: `1px solid ${COLORS.borderFocus}`,
        borderRadius: '10px',
        fontFamily: FONTS.body,
        fontSize: '0.8125rem',
        color: COLORS.textMuted,
        lineHeight: '1.5',
        marginBottom: '1rem',
      }}>
        Every INDI offering requires a dedicated legal entity (typically an LLC) formed specifically for
        the project. This is the "issuer" in SEC terms and is separate from any existing production company.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Entity Legal Name</Label>
          <TextInput value={entity.legalName} onChange={(v) => updateEntity('legalName', v)} placeholder='e.g., "The Last Sunset Productions LLC"' />
        </div>
        <div>
          <Label required>Entity Type</Label>
          <SelectInput value={entity.entityType} onChange={(v) => updateEntity('entityType', v)} options={ENTITY_TYPES} placeholder="Select..." />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label required>Jurisdiction</Label>
          <TextInput value={entity.jurisdiction} onChange={(v) => updateEntity('jurisdiction', v)} placeholder="e.g., Delaware" />
          <HelpText>Delaware recommended for most offerings.</HelpText>
        </div>
        <div>
          <Label required>Date of Organization</Label>
          <input type="date" style={styles.input} value={entity.dateOfOrganization || ''} onChange={(e) => updateEntity('dateOfOrganization', e.target.value)} />
        </div>
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Physical Address</Label>
        <TextInput value={entity.physicalAddress} onChange={(v) => updateEntity('physicalAddress', v)} placeholder="Full business address (P.O. Box not accepted for SEC filings)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
        <div>
          <Label>Entity Website</Label>
          <TextInput value={entity.entityWebsite} onChange={(v) => updateEntity('entityWebsite', v)} placeholder="Auto-populated with indi.space project page" type="url" />
        </div>
        <div>
          <Label required>EIN / Tax ID</Label>
          <TextInput value={entity.ein} onChange={(v) => updateEntity('ein', v)} placeholder="XX-XXXXXXX" />
        </div>
      </div>

      {/* 5.2 Risk Factors */}
      <div style={styles.sectionTitle}>5.2 Risk Factors</div>
      <div style={{
        padding: '0.85rem 1rem',
        background: COLORS.cyanMuted,
        border: `1px solid ${COLORS.borderFocus}`,
        borderRadius: '10px',
        fontFamily: FONTS.body,
        fontSize: '0.8125rem',
        color: COLORS.textMuted,
        lineHeight: '1.5',
        marginBottom: '1rem',
      }}>
        Templates with 15–20 standard risk factors will be provided. Customize with project-specific details.
        Your securities attorney should review the final risk factors.
      </div>

      {[
        { key: 'industryRisks', label: 'Industry Risks', placeholder: 'Market competition, changing audience preferences, distribution challenges, piracy...' },
        { key: 'projectSpecificRisks', label: 'Project-Specific Risks', placeholder: 'Casting uncertainties, location dependencies, weather, key-person risk...' },
        { key: 'financialRisks', label: 'Financial Risks', placeholder: 'Budget overrun risk, revenue uncertainty, no guarantee of returns, illiquidity...' },
        { key: 'regulatoryRisks', label: 'Regulatory & Legal Risks', placeholder: 'Blockchain regulatory uncertainty, securities transfer restrictions...' },
        { key: 'tokenTechRisks', label: 'Token / Technology Risks', placeholder: 'Smart contract risks, blockchain network risks, wallet security...' },
      ].map((field) => (
        <div key={field.key} style={styles.fieldGroup}>
          <Label required>{field.label}</Label>
          <TextArea value={risks[field.key]} onChange={(v) => updateRisks(field.key, v)} placeholder={field.placeholder} rows={4} />
        </div>
      ))}

      {/* 5.3 Financial Condition */}
      <div style={styles.sectionTitle}>5.3 Financial Condition</div>

      <div style={styles.fieldGroup}>
        <Label required>Financial Condition Discussion</Label>
        <TextArea
          value={financial.discussion}
          onChange={(v) => updateFinancial('discussion', v)}
          placeholder="Liquidity, capital resources, and how offering proceeds affect viability."
          rows={4}
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Material Indebtedness</Label>
        <TextArea
          value={financial.materialIndebtednessText}
          onChange={(v) => updateFinancial('materialIndebtednessText', v)}
          placeholder='If none, state "None." Otherwise list: amount, interest rate, maturity, terms.'
          rows={3}
        />
      </div>

      <div style={styles.fieldGroup}>
        <Label required>Prior Offerings (Past 3 Years)</Label>
        <TextArea
          value={financial.priorOfferingsText}
          onChange={(v) => updateFinancial('priorOfferingsText', v)}
          placeholder='If none, state "None." Otherwise list: date, exemption type, amount, use of proceeds.'
          rows={3}
        />
      </div>

      {/* 5.4 Related-Party Transactions */}
      <div style={styles.sectionTitle}>5.4 Related-Party Transactions</div>
      <div style={styles.fieldGroup}>
        <Label required>Related-Party Transaction Disclosure</Label>
        <TextArea
          value={data.relatedPartyTransactionsText}
          onChange={(v) => onChange({ ...data, relatedPartyTransactionsText: v })}
          placeholder='If none, state "None." Otherwise disclose any transaction exceeding 5% of capital with directors, officers, 20%+ owners, or their families.'
          rows={4}
        />
      </div>

      {/* 5.5 Regulatory Disclosures */}
      <div style={styles.sectionTitle}>5.5 Regulatory Disclosures</div>

      <div style={styles.fieldGroup}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={regulatory.badActorDisclosure === false} onChange={(e) => updateRegulatory('badActorDisclosure', !e.target.checked)} style={{ marginTop: '3px' }} />
          <span style={{ fontFamily: FONTS.body, fontSize: '0.875rem', color: COLORS.text, lineHeight: '1.5' }}>
            <strong>Bad Actor Disclosure:</strong> I confirm there are no matters that would trigger disqualification under Rule 503(a).
          </span>
        </label>
      </div>

      <div style={styles.fieldGroup}>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={regulatory.priorReportingFailures === false} onChange={(e) => updateRegulatory('priorReportingFailures', !e.target.checked)} style={{ marginTop: '3px' }} />
          <span style={{ fontFamily: FONTS.body, fontSize: '0.875rem', color: COLORS.text, lineHeight: '1.5' }}>
            <strong>Prior Reporting:</strong> Neither the issuer nor its predecessors have failed to comply with Reg CF ongoing reporting.
          </span>
        </label>
      </div>

      {/* 5.7 Certifications */}
      <div style={styles.sectionTitle}>5.7 Final Certifications</div>

      {[
        {
          key: 'accuracyCertification',
          text: 'I certify that all information provided is true and complete in all material respects. I understand that intentional misstatements or omissions constitute federal criminal violations.',
        },
        {
          key: 'riskAcknowledgment',
          text: 'I understand that securities offered through INDI Platform have not been recommended or approved by the SEC or any state securities authority.',
        },
        {
          key: 'ongoingObligationsAcknowledgment',
          text: 'I understand that INDI will manage SEC filings and ongoing reporting on behalf of my project\'s SPV, and that I am responsible for providing accurate and timely information.',
        },
        {
          key: 'platformServicesAgreement',
          text: 'I acknowledge INDI Platform Fee (5%), Distribution Services Fee, and Compliance Services Fee ($5,000), and agree to INDI Distribution LLC\'s exclusive distribution terms.',
        },
      ].map((cert) => (
        <div key={cert.key} style={{ ...styles.fieldGroup, marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={certs[cert.key] || false}
              onChange={(e) => updateCerts(cert.key, e.target.checked)}
              style={{ marginTop: '3px', accentColor: COLORS.cyan }}
            />
            <span style={{
              fontFamily: FONTS.body,
              fontSize: '0.875rem',
              color: COLORS.text,
              lineHeight: '1.55',
            }}>
              {cert.text} <span style={styles.labelRequired}>*</span>
            </span>
          </label>
        </div>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────
// MAIN WIZARD COMPONENT
// ──────────────────────────────────────────────

const STEP_NAMES = [
  'Project Basics',
  'Team & Credentials',
  'Budget & Financing',
  'Revenue & Distribution',
  'Compliance & Legal',
];

export default function ProjectSubmissionWizard({ projectId, onComplete, onClose }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [stepData, setStepData] = useState({
    1: {}, 2: {}, 3: {}, 4: {}, 5: {},
  });
  const [stepsCompleted, setStepsCompleted] = useState({});
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null); // 'saved' | 'error'
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL;

  // Get auth token
  const getToken = () => {
    try {
      return localStorage.getItem('dynamic_authentication_token')
        || localStorage.getItem('dynamic_token');
    } catch { return null; }
  };

  const authHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // Load existing intake data
  useEffect(() => {
    async function loadIntake() {
      try {
        const res = await fetch(`${API_URL}/intake/${projectId}`, {
          headers: authHeaders(),
        });
        const data = await res.json();
        if (data.success && data.project?.intake) {
          const intake = data.project.intake;
          const loaded = { 1: {}, 2: {}, 3: {}, 4: {}, 5: {} };

          // Step 1
          if (intake.step1) {
            loaded[1] = {
              title: data.project.title,
              tagline: data.project.tagline,
              logline: data.project.logline,
              genre: data.project.genre,
              synopsis: data.project.synopsis,
              ...intake.step1,
            };
          }

          // Steps 2-5
          if (intake.step2) loaded[2] = intake.step2;
          if (intake.step3) loaded[3] = intake.step3;
          if (intake.step4) loaded[4] = intake.step4;
          if (intake.step5) loaded[5] = intake.step5;

          setStepData(loaded);

          // Restore completion state
          const completed = {};
          if (intake.stepsCompleted) {
            for (let i = 1; i <= 5; i++) {
              if (intake.stepsCompleted[`step${i}`]) completed[i] = true;
            }
          }
          setStepsCompleted(completed);

          // Resume at last saved step
          if (intake.lastSavedStep) {
            setCurrentStep(Math.min(intake.lastSavedStep + 1, 5));
          }
        }
      } catch (err) {
        console.error('Failed to load intake data:', err);
      }
      setLoading(false);
    }
    loadIntake();
  }, [projectId]);

  // Auto-save with debounce
  const saveStep = useCallback(async (step, data, validate = false) => {
    setSaving(true);
    setErrors([]);
    setSaveStatus(null);

    try {
      const url = `${API_URL}/intake/${projectId}/step/${step}${validate ? '?validate=true' : ''}`;
      const res = await fetch(url, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(data),
      });

      const result = await res.json();

      if (!result.success) {
        setErrors(result.errors || [result.error]);
        setSaveStatus('error');
        setSaving(false);
        return false;
      }

      setSaveStatus('saved');
      if (result.completion) {
        const completed = {};
        for (let i = 1; i <= 5; i++) {
          if (i <= result.completion.stepsCompleted) completed[i] = true;
        }
        // More accurate: use the step we just saved
        setStepsCompleted(prev => ({ ...prev, [step]: true }));
      }

      setSaving(false);
      return true;
    } catch (err) {
      console.error('Save error:', err);
      setErrors(['Network error. Please try again.']);
      setSaveStatus('error');
      setSaving(false);
      return false;
    }
  }, [projectId, API_URL]);

  const handleNext = async () => {
    const success = await saveStep(currentStep, stepData[currentStep], true);
    if (success && currentStep < 5) {
      setCurrentStep(currentStep + 1);
      setErrors([]);
      setSaveStatus(null);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = async () => {
    // Save current step without validation before going back
    await saveStep(currentStep, stepData[currentStep], false);
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors([]);
      setSaveStatus(null);
      window.scrollTo(0, 0);
    }
  };

  const handleSaveDraft = async () => {
    await saveStep(currentStep, stepData[currentStep], false);
  };

  const handleSubmit = async () => {
    // Validate current step first
    const success = await saveStep(currentStep, stepData[currentStep], true);
    if (!success) return;

    // Submit for review
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/intake/${projectId}/submit`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await res.json();

      if (result.success) {
        if (onComplete) onComplete(result);
      } else {
        setErrors(result.missingSteps
          ? [`Complete steps ${result.missingSteps.join(', ')} before submitting.`]
          : [result.error]
        );
      }
    } catch (err) {
      setErrors(['Failed to submit. Please try again.']);
    }
    setSaving(false);
  };

  const updateCurrentStepData = (newData) => {
    setStepData(prev => ({ ...prev, [currentStep]: newData }));
    setSaveStatus(null);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        fontFamily: FONTS.body,
        color: COLORS.textMuted,
      }}>
        Loading intake form...
      </div>
    );
  }

  const completionPercent = Math.round((Object.keys(stepsCompleted).length / 5) * 100);

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '2rem 1rem',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{
              fontFamily: FONTS.heading,
              fontSize: '1.75rem',
              fontWeight: '700',
              color: COLORS.text,
              margin: 0,
              letterSpacing: '-0.02em',
            }}>
              Project Submission
            </h1>
            <p style={{
              fontFamily: FONTS.body,
              fontSize: '0.9375rem',
              color: COLORS.textMuted,
              margin: '0.4rem 0 0 0',
            }}>
              SEC Regulation CF compliant intake — {completionPercent}% complete
            </p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.textDim,
                fontFamily: FONTS.body,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Step Navigation */}
      <div style={{
        display: 'flex',
        gap: '2px',
        marginBottom: '2rem',
      }}>
        {STEP_NAMES.map((name, i) => {
          const step = i + 1;
          const isActive = step === currentStep;
          const isComplete = stepsCompleted[step];
          return (
            <button
              key={step}
              type="button"
              onClick={async () => {
                if (step !== currentStep) {
                  await saveStep(currentStep, stepData[currentStep], false);
                  setCurrentStep(step);
                  setErrors([]);
                  setSaveStatus(null);
                }
              }}
              style={{
                flex: 1,
                padding: '0.65rem 0.25rem',
                background: isActive
                  ? 'rgba(6, 182, 212, 0.12)'
                  : isComplete
                    ? 'rgba(132, 204, 22, 0.08)'
                    : 'rgba(100, 116, 139, 0.08)',
                border: 'none',
                borderBottom: isActive
                  ? `3px solid ${COLORS.cyan}`
                  : isComplete
                    ? `3px solid ${COLORS.lime}`
                    : `3px solid transparent`,
                color: isActive ? COLORS.cyan : isComplete ? COLORS.lime : COLORS.textDim,
                fontFamily: FONTS.body,
                fontSize: '0.75rem',
                fontWeight: isActive ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 200ms ease',
                borderRadius: '4px 4px 0 0',
              }}
            >
              <span style={{ fontFamily: FONTS.mono, fontSize: '0.6875rem', opacity: 0.7 }}>
                {isComplete ? '✓' : step}
              </span>
              <br />
              {name}
            </button>
          );
        })}
      </div>

      {/* Step Content Card */}
      <div style={{
        background: `linear-gradient(135deg, ${COLORS.bgCard} 0%, ${COLORS.bg} 100%)`,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '16px',
        padding: '2rem',
      }}>
        {/* Step Title */}
        <h2 style={{
          fontFamily: FONTS.heading,
          fontSize: '1.375rem',
          fontWeight: '700',
          color: COLORS.text,
          margin: '0 0 0.25rem 0',
        }}>
          Step {currentStep}: {STEP_NAMES[currentStep - 1]}
        </h2>

        <ErrorBanner errors={errors} />

        {/* Step Components */}
        {currentStep === 1 && <Step1ProjectBasics data={stepData[1]} onChange={updateCurrentStepData} />}
        {currentStep === 2 && <Step2TeamCredentials data={stepData[2]} onChange={updateCurrentStepData} />}
        {currentStep === 3 && <Step3BudgetFinancing data={stepData[3]} onChange={updateCurrentStepData} />}
        {currentStep === 4 && <Step4RevenueDistribution data={stepData[4]} onChange={updateCurrentStepData} />}
        {currentStep === 5 && <Step5ComplianceLegal data={stepData[5]} onChange={updateCurrentStepData} />}

        {/* Navigation */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: '2rem',
          paddingTop: '1.5rem',
          borderTop: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                disabled={saving}
                style={{
                  padding: '0.65rem 1.25rem',
                  background: 'rgba(100, 116, 139, 0.15)',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '10px',
                  color: COLORS.textMuted,
                  fontFamily: FONTS.body,
                  fontWeight: '500',
                  fontSize: '0.9375rem',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.5 : 1,
                }}
              >
                Back
              </button>
            )}
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={saving}
              style={{
                padding: '0.65rem 1.25rem',
                background: 'none',
                border: `1px solid ${COLORS.border}`,
                borderRadius: '10px',
                color: COLORS.textDim,
                fontFamily: FONTS.body,
                fontWeight: '500',
                fontSize: '0.875rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
              }}
            >
              {saving ? 'Saving...' : saveStatus === 'saved' ? '✓ Saved' : 'Save Draft'}
            </button>
          </div>

          {currentStep < 5 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={saving}
              style={{
                padding: '0.65rem 1.75rem',
                background: saving ? 'rgba(6, 182, 212, 0.4)' : COLORS.cyan,
                border: 'none',
                borderRadius: '10px',
                color: COLORS.bg,
                fontFamily: FONTS.body,
                fontWeight: '600',
                fontSize: '0.9375rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 250ms ease',
              }}
            >
              {saving ? 'Saving...' : 'Save & Continue'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              style={{
                padding: '0.65rem 1.75rem',
                background: saving ? 'rgba(132, 204, 22, 0.4)' : COLORS.lime,
                border: 'none',
                borderRadius: '10px',
                color: COLORS.bg,
                fontFamily: FONTS.body,
                fontWeight: '700',
                fontSize: '0.9375rem',
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.5 : 1,
                transition: 'all 250ms ease',
              }}
            >
              {saving ? 'Submitting...' : 'Submit for Review'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
